import { formatDate } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  filter,
  map,
  scan,
  take,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { CallbacksDto, CallbackSchedulesDto } from '../models';
import { IdleService, TimezoneService, WorkService } from '../services';
import { CallbackService } from '../services/callback.service';

@Component({
  selector: 'app-callback',
  templateUrl: './callback.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallbackComponent {
  private searchDate$ = new BehaviorSubject<Date | undefined>(undefined);

  callbackForm: FormGroup;
  scheduleHours: string[] = [];
  scheduledText: string = '';
  showMore: boolean = false;
  viewModel$: Observable<ViewModel>;

  constructor(
    public activeModal: NgbActiveModal,
    private workSvc: WorkService,
    private formBuilder: FormBuilder,
    private tzSvc: TimezoneService,
    private callbackSvc: CallbackService,
    private idleSvc: IdleService
  ) {
    this.scheduledText = '';
    this.callbackForm = this.formBuilder.group({
      callbackdate: '',
      searchdate: formatDate(Date.now(), 'yyyy-MM-dd', 'en'),
    });

    this.viewModel$ = this.initViewModel();

    this.workSvc.master$
      .pipe(
        switchMap((master) =>
          this.tzSvc.getContactUTCOffset$(master?.state!, master?.country)
        ),
        withLatestFrom(this.workSvc.campaignId$),
        filter(
          ([contactoffset, campaignId]) =>
            contactoffset !== undefined && !!campaignId
        ),
        take(1)
      )
      .subscribe(([contactoffset, campaignId]) => {
        const userutcoffset = 0 - new Date().getTimezoneOffset();
        const offsetFromCurrent = userutcoffset - contactoffset!;
        const nowdatetime = formatDate(
          Date.now() + 15000 * 60 + (0 - offsetFromCurrent) * 60000,
          'yyyy-MM-ddTHH:mm',
          'en'
        );
        this.callbackForm.controls['callbackdate'].setValue(nowdatetime);
        this.callbackSvc.listCampaignSchedules(campaignId!, contactoffset!);
      });

    for (let i = 0; i < 23; i++) {
      if (i < 12) this.scheduleHours[i] = i.toString() + ' am';
      else if (i == 12) this.scheduleHours[i] = i.toString() + ' pm';
      else this.scheduleHours[i] = (i - 12).toString() + ' pm';
    }
  }

  @HostListener('keydown')
  keyDown() {
    this.idleSvc.agentInteraction();
  }

  @HostListener('mousedown')
  mouseDown() {
    this.idleSvc.agentInteraction();
  }

  private initViewModel() {
    return merge<Partial<ViewModel>>(
      this.callbackSvc.campaignSchedules$.pipe(
        map((campaignSchedules) => ({ campaignSchedules }))
      ),
      combineLatest([this.callbackSvc.userCallbacks$, this.searchDate$]).pipe(
        map(([userCallbacks, searchDate]) => ({
          callbacks: userCallbacks?.filter(
            (cb) =>
              !searchDate ||
              formatDate(cb.cbDatetime, 'yyyy-MM-dd', 'en') ===
                formatDate(searchDate, 'yyyy-MM-dd', 'en')
          ),
          searchDate,
        }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  cancel() {
    this.activeModal.close({ result: 'Cancel' });
  }

  scheduleCallbacks() {
    let selectedDate = new Date(this.callbackForm.get('callbackdate')?.value);
    let userutcoffset = 0 - new Date().getTimezoneOffset();

    this.workSvc.master$
      .pipe(
        switchMap((master) =>
          this.tzSvc.getContactUTCOffset$(master?.state!, master?.country)
        ),
        withLatestFrom(
          this.workSvc.campaignId$,
          this.workSvc.campaignFinishDate$
        ),
        filter(
          ([contactoffset, campaignId, finishdate]) =>
            contactoffset !== undefined && !!campaignId
        ),
        take(1)
      )
      .subscribe(([contactoffset, campaignId, finishdate]) => {
        let offsetFromCurrent = userutcoffset - contactoffset!;

        let mindatetime = new Date(
          Date.now() + 10000 * 60 + (0 - offsetFromCurrent) * 60000
        );

        const finishDate = new Date(finishdate!);
        if (selectedDate > finishDate) {
          this.scheduledText =
            "You can't schedule a callback after " +
            formatDate(finishDate, 'dd/MM/yyyy', 'en');
        } else if (selectedDate.getTime() > mindatetime.getTime()) {
          this.scheduledText = 'Callback Scheduled';
          this.activeModal.close({
            result: 'Scheduled',
            cbDatetime: this.callbackForm.get('callbackdate')?.value,
          });
        } else {
          this.scheduledText =
            "You can't schedule a callback prior to " +
            formatDate(mindatetime, 'h:mm a', 'en') +
            ' (10 minutes from now).';
        }
      });
  }

  searchCallbacks() {
    let searchDate = this.callbackForm.controls['searchdate'].value;
    if (searchDate) {
      this.searchDate$.next(new Date(searchDate));
    }
  }

  selectCallbackDateTime(selectedDate: Date, selectedTime: number) {
    var newDate = new Date(
      formatDate(selectedDate, 'yyyy-MM-dd', 'en') +
        ' ' +
        selectedTime.toString()
    );

    this.callbackForm.controls['callbackdate'].setValue(
      formatDate(newDate, 'yyyy-MM-ddTHH:mm', 'en')
    );
  }
}

interface ViewModel {
  callbacks: CallbacksDto[] | undefined;
  campaignSchedules: CallbackSchedulesDto | undefined;
  searchDate?: Date;
}
